import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SubsetOfPassengersInPalsBookingWarning from '../../AfterSalesWarning/SubsetOfPassengersInPalsBookingWarning/SubsetOfPassengersInPalsBookingWarning';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';

import { useLazyQuery, useApolloClient } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment';

import { isUserEurostarAgent, getBookingAttachedLoyaltyAccount } from 'utils/booking';
import { COMFORT, PLUS, ER_STANDARD, SP, STANDARD, STD } from 'constants/classOfService';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { getTicketUpgradePricesParams, getUpgradeJourneysFromFares, getUpgradeQuote } from 'utils/upgradePoints';
import { GET_CUSTOMER_DETAILS, GET_EXTRA_PRODUCTS, GET_JOURNEY_SEARCH_FOR_EXCHANGE, GET_TICKET_UPGRADE_INFORMATION } from 'store/queries';
import Modal from 'components/ui/Modal';
import Button from 'components/ui/Button';
import useRebook from 'components/mutations/useRebook';
import { INTENTION_TYPE } from 'constants/search';
import AgentPortalError from 'errors/AgentPortalError';
import { buildESRequest } from 'pages/Exchange/ExchangeDetails/ExchangeConfirmSelection/ExchangeConfirmSelection.utils';
import { InsuranceModal } from '../InsuranceModal/InsuranceModal';

import {
  buildUpgradeSearchPassengers,
  findEarliestLeg,
  getExtrasVariables,
  hasEnoughPoints,
  rebookExtrasRequest
} from './UpgradePointsCta.utils';
import { StyledCtaButtons } from '../AfterSalesCtas.styles';
import {
  runAfterSaleValidator,
  allSegmentsHaveSameSelectedPassengers,
  allSegmentsCanBeRebooked,
  userCanOverrideAfterSalesRules,
  allSegmentsOfType,
  allSegmentsOfService,
  productHasNotBeenPrrRefunded,
  allSegmentsInOneJourney,
  allSelectedProductsConfirmed,
  checkIsEurostarRedAndBlue,
  selectedItemsArePartOfASummatedFare,
  selectedProductsThroughFare,
  getAssociatedItemRefsForSelectedItems,
  getSegmentsForItemRefs,
  selectedItemsHaveLuggageExtras,
  hasActiveInsurance
} from '../AfterSalesCtas.utils';

import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';
import { isSegmentASummatedFare } from 'utils/summatedFares';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

const ErrorButton = styled(Button)`
  margin: auto;
`;
const ModalContainer = styled('div')`
  width: min-content;
  margin: auto;
`;

const UpgradePointsCta = ({
  history: {
    push,
    location: { search, pathname, hash }
  },
  label,
  disabled,
  booking,
  selectedItemRefs,
  user,
  willResultInChildAlone = false,
  exchangingSubsetOfPaxsInPALBooking = false,
  isMenuOpen = false,
  setDisabled
}) => {
  const client = useApolloClient();
  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const attachedMembershipNumber = getBookingAttachedLoyaltyAccount(booking);
  const hasInsurance = runAfterSaleValidator(booking, selectedItemRefs)([hasActiveInsurance]);
  const [error, setError] = useState(null);
  const [showNotEnoughPoints, setShowNotEnoughPoints] = useState(false);
  const [upgradeQuoteLoading, setUpgradeQuoteLoading] = useState(false);
  const [showMissingTrain, setShowMissingTrain] = useState(false);
  const [showMissingTrainError, setShowMissingTrainError] = useState(false);
  const [showNotLoggedIn, setShowNotLoggedIn] = useState(false);
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(false);
  const [warningSubsetOfPaxsInPals, setWarningSubsetOfPaxsInPals] = useState(false);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);
  const [warningInsurance, setWarningInsurance] = useState(false);

  const { outbound, inbound, reference } = booking;
  const filteredOutbound = filterLegsByProductItemRefs(outbound, selectedItemRefs);
  const filteredOutboundPaxs = filteredOutbound
    .map((fo) => fo.bookingItems?.filter((bi) => selectedItemRefs.includes(bi.itemRef)).map((bi) => bi.passengerId))
    .flat();
  const filteredInbound = filterLegsByProductItemRefs(inbound, selectedItemRefs);
  const filteredInboundPaxs = filteredInbound
    .map((fo) => fo.bookingItems?.filter((bi) => selectedItemRefs.includes(bi.itemRef)).map((bi) => bi.passengerId))
    .flat();

  const outboundExtras = filteredOutbound
    .map((fo) => fo.extraProducts || [])
    .flat()
    .filter((ep) => !ep.cancelled && filteredOutboundPaxs.includes(ep.passenger.id));

  const inboundExtras = filteredInbound
    .map((fo) => fo.extraProducts || [])
    .flat()
    .filter((ep) => !ep.cancelled && filteredInboundPaxs.includes(ep.passenger.id));

  const outboundEarliestLeg = findEarliestLeg(filteredOutbound);
  const inboundEarliestLeg = findEarliestLeg(filteredInbound);

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  const redirectOnCompleted = () =>
    push(
      `/exchange/${booking.reference}/confirm?currency=${booking.currency}&isUpgradeWithPoints=true${
        membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
      }`
    );

  const showMissingTrainModal = (isInbound, direction) => {
    setShowMissingTrainError({
      isInbound,
      trainNumber: direction.trainNumber,
      date: moment(direction.timing.date).format('DD MMM YYYY')
    });
    setShowMissingTrain(true);
  };

  const getDirection = (direction, searchDirection, isInbound) => {
    if (!direction) {
      return null;
    }

    const journey = searchDirection.journeys?.find((jrn) =>
      jrn.fares?.find(
        (fare) =>
          [SP, COMFORT, PLUS].includes(fare.classOfService?.code) &&
          fare.legs?.find(
            (leg) =>
              leg.trainNumber === direction.trainNumber &&
              leg.destination.uic === direction.destination.uic &&
              leg.origin.uic === direction.origin.uic
          )
      )
    );

    if (!journey) {
      showMissingTrainModal(isInbound, direction);

      return null;
    }

    const fare = journey.fares.find(
      (fr) => [SP, COMFORT, PLUS].includes(fr.classOfService.code) && fr.bookingItems.length > 0 && fr.canBeBooked
    );

    if (!fare) {
      showMissingTrainModal(isInbound, direction);

      return null;
    }

    const newFare = { ...fare };
    newFare.direction = isInbound ? 'inbound' : 'outbound';

    return newFare;
  };

  const [rebook, { loading: rebookLoading }] = useRebook();

  const [getCustomerInfo, { called: customerInfoCalled, loading: customerInfoLoading, data: customerData }] =
    useLazyQuery(GET_CUSTOMER_DETAILS);
  const customerInfo = customerData?.getCustomerDetails;

  useEffect(() => {
    if (membershipNumber) {
      getCustomerInfo({
        variables: {
          membershipNumber
        }
      });
    }
  }, [getCustomerInfo, membershipNumber]);

  const handleExtrasFetch = async (currency, fare, extras) => {
    const extrasVariables = {
      currency,
      ...getExtrasVariables(fare, extras)
    };

    if (extrasVariables.passengers.length > 0 && extrasVariables.types.length > 0) {
      const results = await client.query({
        query: GET_EXTRA_PRODUCTS,
        variables: extrasVariables
      });

      return results.data?.getExtraProducts;
    }

    return [];
  };

  const fetchExtras = async (data) => {
    const outboundExtrasAvailability =
      outboundExtras.length > 0 ? await handleExtrasFetch(data?.currency, data?.outboundSelectedFare, outboundExtras) : [];

    const inboundExtrasAvailability =
      inboundExtras.length > 0 ? await handleExtrasFetch(data?.currency, data?.inboundSelectedFare, inboundExtras) : [];

    const outboundRebookExtras = rebookExtrasRequest(outboundExtrasAvailability, outboundExtras);
    const inboundRebookExtras = rebookExtrasRequest(inboundExtrasAvailability, inboundExtras);

    return {
      outboundRebookExtras,
      inboundRebookExtras
    };
  };

  const requestUpgrade = async (journeySearchData) => {
    const data = {
      outboundSelectedFare: getDirection(outboundEarliestLeg, journeySearchData.journeySearchForExchange.outbound, false),
      inboundSelectedFare: getDirection(inboundEarliestLeg, journeySearchData.journeySearchForExchange.inbound, true),
      currency: booking.currency,
      reference: booking.reference,
      existingTrains: {}
    };

    if ((outboundEarliestLeg && !data.outboundSelectedFare) || (inboundEarliestLeg && !data.inboundSelectedFare)) {
      return;
    }

    let upgradeQuote;

    setUpgradeQuoteLoading(true);

    try {
      const journeys = getUpgradeJourneysFromFares([data.outboundSelectedFare, data.inboundSelectedFare], booking.passengers);
      const { data: pricesData } = await client.query({
        query: GET_TICKET_UPGRADE_INFORMATION,
        variables: getTicketUpgradePricesParams(journeys),
        fetchPolicy: 'network-only'
      });

      upgradeQuote = getUpgradeQuote(journeys, pricesData.getTicketUpgradeInformation);
    } catch (e) {
      setError(e);

      return;
    } finally {
      setUpgradeQuoteLoading(false);
    }

    if (!hasEnoughPoints(upgradeQuote.points, customerInfo)) {
      setShowNotEnoughPoints(true);

      return;
    }

    const extrasRequests = await fetchExtras(data);

    const esRequest = buildESRequest({
      data,
      overrideReason: undefined,
      isFlowForward: false,
      isExchangingOFETrain: false,
      outboundExtras: extrasRequests.outboundRebookExtras,
      inboundExtras: extrasRequests.inboundRebookExtras,
      isUpgradeWithPointRequest: true
    });

    try {
      await rebook({
        variables: esRequest
      });
      redirectOnCompleted();
    } catch (e) {
      setError(e);
    }
  };

  const [journeySearch, { loading: journeySearchLoading }] = useLazyQuery(GET_JOURNEY_SEARCH_FOR_EXCHANGE, {
    onCompleted: (data) => requestUpgrade(data)
  });

  const isLoading = customerInfoLoading || journeySearchLoading || upgradeQuoteLoading;

  const isExchangePossible =
    selectedItemRefs.length > 0 &&
    runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      allSegmentsHaveSameSelectedPassengers,
      allSelectedProductsConfirmed,
      allSegmentsOfType([STD, ER_STANDARD, STANDARD]),
      allSegmentsInOneJourney,
      allSegmentsOfService([serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED]),
      productHasNotBeenPrrRefunded
    ]) &&
    runAfterSaleValidator(booking, selectedItemRefs, user, 'some')([allSegmentsCanBeRebooked, userCanOverrideAfterSalesRules]) &&
    isUserEurostarAgent(user) &&
    !booking.isLoyalty;

  const isOutboundSummatedFare = isSegmentASummatedFare(booking.outbound);
  const isInboundSummatedFare = isSegmentASummatedFare(booking.inbound);
  const areItemsInASummatedFare = selectedItemsArePartOfASummatedFare(
    booking,
    selectedItemRefs,
    isOutboundSummatedFare,
    isInboundSummatedFare
  );

  const associatedItemsRefs = getAssociatedItemRefsForSelectedItems({
    booking,
    selectedItemRefs
  });

  const segments = [...getSegmentsForItemRefs(booking, [...selectedItemRefs, ...associatedItemsRefs])];

  const disableUpgradeForSummatedESSN =
    areItemsInASummatedFare && segments.some((segment) => segment.serviceType?.code === 'SN' || segment.serviceType?.code === 'SNCB');

  useEffect(() => {
    setDisabled(!isExchangePossible || disableUpgradeForSummatedESSN);
  });

  const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;

  const sendJourneySearch = () => {
    const variables = {
      reference,
      intention: INTENTION_TYPE.UPGRADE,
      exchangeItems: selectedItemRefs.map(Number),
      currency: booking.currency,
      passengers: buildUpgradeSearchPassengers(booking, selectedItemRefs),
      useNewLoyaltySystem: true
    };

    const outboundLastLeg = filteredOutbound[filteredOutbound.length - 1];
    const inboundLastLeg = filteredInbound[filteredInbound.length - 1];

    if (outboundEarliestLeg) {
      variables.origin = outboundEarliestLeg.origin.uic;
      variables.destination = outboundLastLeg.addOnLeg ? outboundLastLeg.addOnLeg.destination.uic : outboundLastLeg.destination.uic;
      variables.outboundDate = outboundEarliestLeg.timing.date;

      if (inboundEarliestLeg) {
        variables.inboundDate = inboundEarliestLeg.timing.date;
        variables.returningOrigin = inboundEarliestLeg.addOnLeg ? inboundEarliestLeg.addOnLeg.origin.uic : inboundEarliestLeg.origin.uic;
        variables.returningDestination = inboundLastLeg.destination.uic;
      }
    } else {
      variables.origin = inboundEarliestLeg.origin.uic;
      variables.destination = inboundLastLeg.addOnLeg ? inboundLastLeg.addOnLeg.destination.uic : inboundLastLeg.destination.uic;
      variables.inboundDate = inboundEarliestLeg.timing.date;
    }

    const areItemsInAThroughFare = selectedProductsThroughFare(selectedItemRefs, booking);

    const isEurostarRedAndBlue = checkIsEurostarRedAndBlue([...booking.outbound, ...booking.inbound]);

    if (isEurostarRedAndBlue && (areItemsInASummatedFare || areItemsInAThroughFare)) {
      variables.applySummatedFaresMaxTransferRules = true;
      variables.multipleFlexibility = true;
    }

    journeySearch({
      variables
    });
  };

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (!customerInfoCalled || !customerInfo) {
      setShowNotLoggedIn(true);

      return;
    }

    if (isWrongAccountLoggedIn) {
      setShowNotLoggedIn(true);

      return;
    }

    if (exchangingSubsetOfPaxsInPALBooking) {
      return setWarningSubsetOfPaxsInPals(true);
    }

    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    if (hasInsurance) {
      setWarningInsurance(true);
      return;
    }

    sendJourneySearch();
  };

  const handleClickWithoutPals = () => {
    if (disabled) {
      return;
    }

    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (!customerInfoCalled || !customerInfo) {
      setShowNotLoggedIn(true);

      return;
    }

    if (isWrongAccountLoggedIn) {
      setShowNotLoggedIn(true);

      return;
    }

    sendJourneySearch();
  };

  const toggleNotEnoughPointsModal = () => setShowNotEnoughPoints(!showNotEnoughPoints);
  const toggleNotLoggedInModal = () => setShowNotLoggedIn(!showNotLoggedIn);
  const toggleMissingTrainModal = () => setShowMissingTrain(!showMissingTrain);
  const toggleInsuranceModal = () => setWarningInsurance(!warningInsurance);

  if (error) {
    throw new AgentPortalError(error);
  }

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
  };

  return (
    <>
      <Modal isOpen={showNotEnoughPoints} onClose={toggleNotEnoughPointsModal}>
        <FormattedMessage id="upgradePoints.notEnoughPoints" />
      </Modal>
      <Modal isOpen={showNotLoggedIn} onClose={toggleNotLoggedInModal}>
        <ModalContainer>
          <ErrorButton
            onClick={() =>
              push(`/clubeurostar/manage`, {
                prevPath: pathname,
                prevSearch: search,
                prevHash: hash
              })
            }
            id="error-page-login-cta"
          >
            <FormattedMessage id={isWrongAccountLoggedIn ? 'upgradePoints.wrongLoggedInAccount' : 'upgradePoints.notLoggedIn'} />
          </ErrorButton>
        </ModalContainer>
      </Modal>
      <Modal isOpen={showMissingTrain} onClose={toggleMissingTrainModal}>
        <FormattedMessage
          id={`upgradePoints.${showMissingTrainError.isInbound ? 'missingTrainInbound' : 'missingTrainOutbound'}`}
          values={{
            trainNumber: showMissingTrainError.trainNumber,
            date: showMissingTrainError.date
          }}
        />
      </Modal>
      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />
      {warningInsurance && (
        <InsuranceModal
          title={<FormattedMessage id={'exchanges.insuranceWarning.title'} />}
          onConfirmClick={sendJourneySearch}
          isOpen={warningInsurance}
          onClose={toggleInsuranceModal}
          subtitle="insurance.warningUpgrade"
        />
      )}

      {warningSubsetOfPaxsInPals ? (
        <SubsetOfPassengersInPalsBookingWarning
          isOpen={true}
          closeModal={() => setWarningSubsetOfPaxsInPals(false)}
          handleClick={() => handleClickWithoutPals()}
          loading={isLoading || rebookLoading}
          isDisabled={disabled || rebookLoading || isLoading}
        />
      ) : (
        <StyledCtaButtons
          type="button"
          data-cy="upgrade-points-cta"
          disabled={disabled || rebookLoading || isLoading}
          label={label}
          loading={isLoading || rebookLoading}
          onClick={handleClick}
          isMenuOpen={isMenuOpen}
        >
          {label}
        </StyledCtaButtons>
      )}
    </>
  );
};

UpgradePointsCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  willResultInChildAlone: PropTypes.bool,
  exchangingSubsetOfPaxsInPALBooking: PropTypes.bool,
  isMenuOpen: PropTypes.bool,
  setDisabled: PropTypes.func
};

export default UpgradePointsCta;
